// In-place edits on the v2 render tree — the no-code builder's edit model
// (oxjob #428 iter 22, decision B). The server-rendered `oql_render_v2` tree is
// the single source of truth; the builder mutates nodes here, then rebuilds the
// OQO (v2ToOqo.js) and re-renders through the server, which returns a fresh,
// canonical tree + `lines`. So these mutations only need to produce a VALID OQO
// — the server re-canonicalizes (NNF, factoring, layout) on the next render.
//
// Node shapes are exactly oql_render_v2's (see v2ToOqo.js). Two value-bearing
// token flavors share editing here:
//   - factored clause: value is a `vgroup` of `vleaf`s (each its own node/id)
//   - simple clause:   no value vgroup; the scalar lives on `clause.leaf`, and
//                      its value token carries the CLAUSE id
// so an edit addressed by token id may land on a vleaf OR a simple clause.
//
// DRAFTS: an incomplete clause being created has no server representation (the
// server only ever renders complete queries). Drafts live in a local array,
// render via tokensForDraft(), and fold into the query once they have a value.

import { isSearchColumn, searchSurfaceToFilter, searchBaseColumn, searchColumnSuffix } from "@/components/OqlPlayground/oqoTree";
import { parseValueExpr } from "@/components/Oql/valueExpr";

let _seq = 1;
const eid = () => `e${_seq++}`;

function vleaf(value = "", display = null, negated = false) {
  return { node: "vleaf", id: eid(), value,
           display: display == null ? String(value) : display, negated: !!negated };
}

// The display text a SIMPLE clause was showing for its leaf value — for carrying onto
// the vleaf when a promote/detach converts the clause to a factored shape (oxjob #560).
// The raw leaf value (e.g. `institutions/I136199984`) is NOT what the user was seeing:
// the human form is baked into the server-rendered `segments`, which the promote
// discards — an ENTITY name rides in the trailing `id` segment (`[Harvard University]`,
// meta.entity_display_name); a search surface (`stemmed "…"`) is the `value` segment's
// text. Without this carry the chip falls back to the raw value until the next server
// render (the raw-id flash Jason's QA caught).
function leafDisplay(c) {
  const segs = c.segments || [];
  const idSeg = segs.find((s) => s && s.kind === "id");
  if (idSeg) {
    const name = (idSeg.meta && idSeg.meta.entity_display_name)
      || String(idSeg.text == null ? "" : idSeg.text).trim().replace(/^\[|\]$/g, "");
    if (String(name).trim()) return String(name);
  }
  const seg = segs.find((s) => s && s.kind === "value");
  if (seg && String(seg.text == null ? "" : seg.text).trim()) return String(seg.text);
  const lf = c.leaf || {};
  return lf.display != null ? lf.display : null;
}

// ---- locate a node by id, anywhere in the tree (or in the draft list) -------
// Returns { node, kind, detach() } where detach() removes the node from its
// parent (splicing an array child, or deleting a clause's `value`, or nulling a
// single top-level `where`). null if not found.
export function locate(tree, id, drafts = []) {
  let res = null;

  const inValue = (v, detach) => {
    if (res) return;
    if (v.id === id) { res = { node: v, kind: v.node, detach }; return; }
    if (v.node === "vgroup")
      v.children.forEach((c, i) => inValue(c, () => v.children.splice(i, 1)));
  };
  const inExpr = (n, detach) => {
    if (res) return;
    if (n.id === id) { res = { node: n, kind: n.node, detach }; return; }
    if (n.node === "clause" && n.value) inValue(n.value, () => { delete n.value; });
    if (n.node === "group")
      n.children.forEach((c, i) => inExpr(c, () => n.children.splice(i, 1)));
  };

  if (tree) {
    const w = tree.where;
    if (w) {
      if (w.node === "group" && w.implicit)
        w.children.forEach((c, i) => inExpr(c, () => w.children.splice(i, 1)));
      else inExpr(w, () => { tree.where = null; });
    }
  }
  // drafts (local, incomplete clauses)
  for (let di = 0; di < drafts.length && !res; di++) {
    const d = drafts[di];
    if (d.id === id) { res = { node: d, kind: "clause", detach: () => drafts.splice(di, 1) }; continue; }
    if (d.value) inValue(d.value, () => { delete d.value; });
  }
  return res;
}

// ---- value edits (addressed by a vbrick token id) ---------------------------

// Set a scalar/search/entity value. Routes search surface forms to the right
// `.search`/`.search.exact` column (quotes/wildcards), and coerces numerics.
export function setValue(tree, id, raw, { numeric = false } = {}, drafts = []) {
  const hit = locate(tree, id, drafts);
  if (!hit) return;
  const coerce = (val) =>
    numeric && typeof val === "string" && val.trim() !== "" && !isNaN(Number(val))
      ? Number(val) : val;
  if (hit.kind === "vleaf") {
    hit.node.value = coerce(raw);
    hit.node.display = String(raw);
    // A vleaf holds the user's LITERAL typed text until the OQO build routes its search
    // surface form (quotes/wildcards/`stemmed`) to the right column (valueToFilter /
    // draftToFilter). Mark it so the chip renders the text verbatim instead of deriving
    // a `stemmed "…"` surface off the clause's still-unrouted `.search` column — that
    // rewrite fed back into the edit box and silently converted a typed exact phrase
    // into a stemmed one (oxjob #560 bug 2). Server reseeds replace the node wholesale,
    // so the marker never survives a round-trip.
    hit.node._rawInput = true;
  } else if (hit.kind === "clause" && hit.node.leaf) {
    const leaf = hit.node.leaf;
    if (isSearchColumn(leaf.column_id)) {
      const r = searchSurfaceToFilter(raw, leaf.column_id);
      leaf.column_id = r.column_id; leaf.value = r.value;
    } else {
      leaf.value = coerce(raw);
    }
  }
}

// ---- value-chip decomposition (oxjob #507 Phases 5 + 6) ---------------------
// Turn a value AST (from valueExpr.parseValueExpr) into real value nodes: a leaf
// becomes a vleaf, a group becomes a vgroup. Numeric columns coerce each leaf to a
// Number when it parses cleanly (so `2020 or 2021` lands as numbers, like setValue).
function astToValueNode(ast, numeric) {
  if (ast.t === "group") {
    return { node: "vgroup", id: eid(), join: ast.join,
             children: ast.children.map((c) => astToValueNode(c, numeric)) };
  }
  const raw = ast.value;
  const val = numeric && typeof raw === "string" && raw.trim() !== "" && !isNaN(Number(raw))
    ? Number(raw) : raw;
  const v = vleaf(val, String(raw), ast.negated);
  v._rawInput = true; // literal typed text, not yet surface-routed (see setValue)
  return v;
}

// Decompose a value chip whose typed text is a boolean expression — `cancer or
// tumor or neoplasm`, `a and b`, or an explicitly parenthesized `(a or b) and c`
// (Phase 6) — into the matching value tree, in place of the single literal value.
// Addressed by the value's token id (a vleaf id, or a SIMPLE clause's id — see the
// file header). Returns true when it actually decomposed; false (a no-op) when the
// text is a single plain value, so the caller falls back to setValue.
//
// Placement of the parsed subtree mirrors the existing value-edit shapes:
//   - SIMPLE clause (sole value, leaf)  -> becomes FACTORED: clause.value = subtree
//     (valueToFilter re-derives each value's .search surface + negation on build).
//   - FACTORED vleaf inside a vgroup    -> replace it at its slot; if the subtree's
//     top join MATCHES the owning vgroup's join, splice the children in flat so
//     `a or b or c` are siblings (not a redundant nested group the server would
//     re-flatten anyway).
//   - clause whose value IS a bare vleaf -> replace clause.value with the subtree.
export function decomposeValue(tree, id, text, { numeric = false } = {}, drafts = []) {
  const ast = parseValueExpr(text);
  if (!ast || ast.t !== "group") return false;          // single value → caller's setValue
  const hit = locate(tree, id, drafts);
  if (!hit) return false;
  const node = astToValueNode(ast, numeric);

  if (hit.kind === "clause") {
    const c = hit.node;
    c.value = node;                                       // factored; valueToFilter handles it
    delete c.leaf;
    return true;
  }
  if (hit.kind === "vleaf") {
    const grp = findVGroupOf(tree, id, drafts);
    if (grp) {
      const i = grp.children.findIndex((x) => x.id === id);
      const at = i < 0 ? grp.children.length : i;
      if (node.node === "vgroup" && node.join === grp.join) {
        grp.children.splice(at, 1, ...node.children);     // flatten same-join siblings
      } else {
        grp.children.splice(at, 1, node);
      }
      return true;
    }
    const sc = soleValueClause(tree, id, drafts);         // clause.value is a bare vleaf
    if (sc) { sc.value = node; return true; }
  }
  return false;
}

// The clause whose DIRECT value node (vleaf OR vgroup) has this id — i.e. `clause.value.id === id`.
function clauseOwningValue(tree, id, drafts = []) {
  let res = null;
  const inExpr = (n) => {
    if (res) return;
    if (n.node === "clause") { if (n.value && n.value.id === id) res = n; }
    else if (n.node === "group") n.children.forEach(inExpr);
  };
  const w = tree && tree.where;
  if (w) { (w.node === "group" && w.implicit) ? w.children.forEach(inExpr) : inExpr(w); }
  drafts.forEach((d) => { if (d.value && d.value.id === id) res = d; });
  return res;
}

// Replace a whole value SUBTREE (addressed by its node id — typically a vgroup behind a
// text-block chip, #523 round 2) with the parse of `text`. Unlike decomposeValue (which splits
// one vleaf), this swaps the entire node: the text-block escape hatch lets the user retype an
// arbitrary value expression (`(a & b)`, `a or b`, nested). A pure-OR parse that matches the
// owning group's join is spliced in flat (so it unpacks back into sibling blocks); otherwise the
// new node takes the slot. Empty/invalid text leaves a single empty value (the swap strips it).
export function setValueExpr(tree, id, text, { numeric = false } = {}, drafts = []) {
  const ast = parseValueExpr(text);
  const node = ast ? astToValueNode(ast, numeric) : vleaf("");
  // The node is the clause's whole value (a text-block that IS clause.value).
  const oc = clauseOwningValue(tree, id, drafts);
  if (oc) { oc.value = node; delete oc.leaf; return true; }
  // The node lives inside a parent vgroup (e.g. `pie or (tart & pastry)`) → replace at its slot.
  const grp = findVGroupOf(tree, id, drafts);
  if (grp) {
    const i = grp.children.findIndex((x) => x.id === id);
    const at = i < 0 ? grp.children.length : i;
    if (node.node === "vgroup" && node.join === grp.join) grp.children.splice(at, 1, ...node.children);
    else grp.children.splice(at, 1, node);
    return true;
  }
  return false;
}

// Set an entity value (chip pick): value is the openalex id, display the name.
export function setEntityValue(tree, id, openalexId, displayName, drafts = []) {
  const hit = locate(tree, id, drafts);
  if (!hit) return;
  if (hit.kind === "vleaf") {
    hit.node.value = openalexId;
    hit.node.display = displayName || String(openalexId);
    hit.node.entity = { id: openalexId, display_name: displayName };
  } else if (hit.kind === "clause" && hit.node.leaf) {
    hit.node.leaf.value = openalexId;
  }
}

// Set a boolean value (true/false). Booleans are always a simple clause.
export function setBool(tree, id, val, drafts = []) {
  const hit = locate(tree, id, drafts);
  if (!hit) return;
  if (hit.kind === "clause" && hit.node.leaf) hit.node.leaf.value = val;
  else if (hit.kind === "vleaf") { hit.node.value = val; hit.node.display = String(val); }
}

// Change a committed clause's relational operator (the op chip). Addressed by the
// clause id. The operator must reach the OQO: a FACTORED clause carries it on the
// clause node (v2ToOqo passes clause.operator down to every leaf); a SIMPLE clause
// emits its raw `leaf` dict verbatim, so the operator must live on `leaf.operator`.
// `unary` ops (e.g. "is unknown") drop the value (OQO leaf value = null); turning a
// unary clause back to binary leaves an empty value for the user to fill.
export function setOperator(tree, id, { op, unary } = {}, drafts = []) {
  const hit = locate(tree, id, drafts);
  if (!hit || hit.kind !== "clause") return;
  const c = hit.node;
  const newOp = op || "is";
  c.operator = newOp;
  if (unary) {
    delete c.value;
    c.leaf = { column_id: c.column_id, value: null, operator: newOp };
    return;
  }
  if (c.value) { return; }                 // factored: operator on clause is enough
  if (!c.leaf) c.leaf = { column_id: c.column_id, value: "" };
  if (c.leaf.value === null || c.leaf.value === undefined) c.leaf.value = "";
  c.leaf.operator = newOp;
}

// Re-point a SEARCH clause to a sibling search surface (title <-> abstract <-> full
// text), keyed by the clause id — the field chip's only field swap (changing any
// other field's property would make its existing values meaningless). `newColumnId`
// is a base `.search` column from searchFieldSiblings(). Analogous to setOperator:
// it rewrites the clause's column AND, for a SIMPLE clause, the raw leaf's column —
// swapping only the BASE while preserving each column's own `.search`/`.search.exact`
// surface suffix (one row can mix exact + stemmed values, e.g.
// `has (amphibian or "amphibi*")`). A FACTORED clause carries no per-value
// column_id: each value's surface is re-derived from its text at OQO-build time
// (v2ToOqo.valueToFilter -> searchSurfaceToFilter), so swapping the clause base is
// enough — the suffixes follow automatically. No-op on a non-search clause.
export function setColumn(tree, id, newColumnId, drafts = []) {
  const hit = locate(tree, id, drafts);
  if (!hit || hit.kind !== "clause") return;
  const c = hit.node;
  if (!isSearchColumn(c.column_id) || !isSearchColumn(newColumnId)) return;
  const newBase = searchBaseColumn(newColumnId);
  const reBase = (col) => `${newBase}${searchColumnSuffix(col)}`;
  c.column_id = reBase(c.column_id);
  if (c.column) c.column = newBase;                       // display name follows on re-render
  if (c.leaf && c.leaf.column_id) c.leaf.column_id = reBase(c.leaf.column_id);
}

// Toggle the negation bit on a value (vleaf) or a simple clause's leaf.
export function toggleNeg(tree, id, drafts = []) {
  const hit = locate(tree, id, drafts);
  if (!hit) return;
  if (hit.kind === "vleaf") hit.node.negated = !hit.node.negated;
  else if (hit.kind === "clause" && hit.node.leaf)
    hit.node.leaf.is_negated = !hit.node.leaf.is_negated;
}

// SET the negation bit to a specific value (vs toggleNeg's flip). Idempotent — used where the
// desired state is known: the entity picker's "not" footer (a checkbox reflects a state, not a
// flip), and the text-chip `not foo` parse (always negate). (#523 round 3.)
export function setNeg(tree, id, neg, drafts = []) {
  const hit = locate(tree, id, drafts);
  if (!hit) return;
  if (hit.kind === "vleaf") hit.node.negated = !!neg;
  else if (hit.kind === "clause" && hit.node.leaf) hit.node.leaf.is_negated = !!neg;
}

// Flip a connector (and <-> or) on a group or value vgroup. The implicit top-level
// `where` group isn't reachable via locate() (which recurses straight into its
// children), so handle it directly — the top-level join (a top-level OR wraps the
// body in a cross-field `(a or b)` group row) must be toggleable too (oxjob #475).
export function toggleJoin(tree, id, drafts = []) {
  const w = tree && tree.where;
  if (w && w.node === "group" && w.id === id) {
    if (w.join) w.join = w.join === "and" ? "or" : "and";
    return;
  }
  const hit = locate(tree, id, drafts);
  if (!hit) return;
  if (hit.node.join) hit.node.join = hit.node.join === "and" ? "or" : "and";
}

// ---- connector-as-unit editing (oxjob #507 Phase 3) -------------------------
// Flip ONE connector (the `&`/`or` between two adjacent operands) and let standard
// precedence (#503: NOT > AND > OR) restructure the group LOCALLY — e.g. in
// `a or b or c`, flipping the connector before `c` to AND yields `a or (b and c)`;
// flipping the connector before `b` yields `(a and b) or c`. This is the
// connector-centric edit model: the user edits the connector, not a clause's join.
//
// Because canonical groups are SINGLE-JOIN (a mixed expression is always a nested
// precedence tree, never one group with mixed connectors), the group reaching here
// has every connector equal to `group.join`. Flipping the k-th connector therefore
// gives the operand sequence ONE differing operator; we re-segment that sequence by
// precedence (OR is the lowest-precedence split; consecutive ANDs bind into a
// subgroup) and rebuild the group as the resulting tree. Works for both expr groups
// (`group`/`vgroup` factory chosen by node kind) and value vgroups.
//
// `groupId` addresses the group (incl. the implicit `where` root); `opIndex` (1..n-1)
// is the index of the operand whose PRECEDING connector was clicked — exactly the
// child index in the group's `children` array. Returns true on a real restructure.
function restructureAtConnector(group, opIndex, makeGroupNode) {
  const kids = group.children || [];
  const n = kids.length;
  if (opIndex < 1 || opIndex >= n) return false;
  const flipped = group.join === "and" ? "or" : "and";
  // operator before child i (i = 1..n-1): the group join, except the flipped one.
  const opBefore = (i) => (i === opIndex ? flipped : group.join);
  // segment the operand run by OR (lowest precedence); consecutive ANDs stay together.
  const segs = [[kids[0]]];
  for (let i = 1; i < n; i++) {
    if (opBefore(i) === "or") segs.push([kids[i]]);
    else segs[segs.length - 1].push(kids[i]);
  }
  const orChildren = segs.map((seg) =>
    seg.length === 1 ? seg[0] : makeGroupNode("and", seg));
  if (orChildren.length === 1) {
    // a single OR segment ⇒ the whole group collapsed to one AND group: adopt it.
    const only = orChildren[0];
    group.join = "and";
    group.children = only.children;
  } else {
    group.join = "or";
    group.children = orChildren;
  }
  return true;
}

export function flipConnector(tree, groupId, opIndex, drafts = []) {
  if (opIndex == null) return false;
  const node = findNodeById(tree, groupId, drafts);
  if (!node) return false;
  if (node.node === "vgroup") {
    return restructureAtConnector(node, opIndex,
      (join, children) => ({ node: "vgroup", id: eid(), join, children }));
  }
  if (node.node === "group") {
    return restructureAtConnector(node, opIndex,
      (join, children) => ({ node: "group", id: eid(), join, paren: true, children }));
  }
  return false;
}

// Negate a whole parenthesized group (the paren-block toolbar's `[not]`), keyed by
// the paren token id. Two cases, because the OQO carries negation differently for
// the two group flavors (NNF: negation never lives on a value vgroup):
//
//   - CLAUSE group  -> set `group.negated`. The server's NNF render shape for a
//     negated group is an OUTER `negated:true` group wrapping a SINGLE plain inner
//     group (oql_render_v2._expr_node); v2ToOqo.exprToFilter reads that exact shape
//     (`is_negated` from the wrapper, join/filters from the inner). So to negate we
//     WRAP the group's children in a fresh inner group; to un-negate we UNWRAP it.
//     The paren token on an already-negated group carries the INNER group's id (the
//     `not ` chrome carries the outer id) — handle both by checking the parent.
//   - VALUE vgroup  -> De Morgan in place: flip every join and toggle `negated` on
//     every descendant leaf (a vgroup has no `negated` flag). The server re-
//     canonicalizes on the next render.
export function negateGroup(tree, id, drafts = []) {
  const hit = locate(tree, id, drafts);
  if (!hit) return;
  if (hit.kind === "vgroup") { deMorgan(hit.node); return; }
  if (hit.kind !== "group") return;

  const g = hit.node;
  if (g.negated) { unwrapNegated(g); return; }    // clicked the `not` chrome (outer id)

  // un-negated group: if it is the lone inner child of a negated parent, the user
  // clicked the paren of an already-negated group -> un-negate by unwrapping the parent.
  const parent = findExprParent(tree, id);
  if (parent && parent.node === "group" && parent.negated &&
      parent.children.length === 1 && parent.children[0] === g) {
    unwrapNegated(parent);
    return;
  }
  wrapNegated(g);
}

function wrapNegated(g) {
  const inner = { node: "group", id: eid(), join: g.join, negated: false,
                  paren: true, children: g.children };
  g.negated = true;
  g.children = [inner];
}

function unwrapNegated(g) {
  g.negated = false;
  const inner = (g.children && g.children.length === 1 && g.children[0].node === "group")
    ? g.children[0] : null;
  if (inner) { g.join = inner.join; g.children = inner.children; }
}

// Full negation of a value vtree via De Morgan: flip the join at every level and
// toggle the `negated` bit on every scalar leaf. NOT(a OR b) == (NOT a AND NOT b);
// recursion handles nested vgroups, e.g. NOT((a or b) and c) == ((not a and not b) or not c).
function deMorgan(node) {
  if (node.node === "vgroup") {
    node.join = node.join === "and" ? "or" : "and";
    node.children.forEach(deMorgan);
  } else {
    node.negated = !node.negated;
  }
}

// Find the expr-tree parent of a node id (group children only — clauses/values are
// leaves for this purpose). Returns the parent expr node, or null at the top level.
function findExprParent(tree, id) {
  let res = null;
  const walk = (n, parent) => {
    if (res) return;
    if (n.id === id) { res = parent; return; }
    if (n.node === "group") n.children.forEach((c) => walk(c, n));
  };
  const w = tree && tree.where;
  if (w) {
    if (w.node === "group" && w.implicit) w.children.forEach((c) => walk(c, w));
    else walk(w, null);
  }
  return res;
}

// Remove a node. A value brick: drop it from its vgroup (and if that empties the
// clause's values, drop the clause). A clause/group row: drop the row.
export function removeNode(tree, id, drafts = []) {
  const hit = locate(tree, id, drafts);
  if (!hit) return;
  hit.detach();
  pruneEmpty(tree);
}

// After a removal, drop any clause whose value vgroup went empty, and collapse a
// vgroup with a single child up into its parent slot (server would anyway).
function pruneEmpty(tree) {
  const visitExpr = (n, parentArr, idx) => {
    if (n.node === "clause" && n.value && n.value.children && !n.value.children.length) {
      if (parentArr) parentArr.splice(idx, 1);
      return;
    }
    if (n.node === "group") {
      for (let i = n.children.length - 1; i >= 0; i--) visitExpr(n.children[i], n.children, i);
    }
  };
  const w = tree && tree.where;
  if (!w) return;
  if (w.node === "group" && w.implicit) {
    for (let i = w.children.length - 1; i >= 0; i--) visitExpr(w.children[i], w.children, i);
    if (!w.children.length) tree.where = null;
  } else {
    visitExpr(w, null, -1);
  }
}

// ---- move a logical ROW (drag-and-drop reorder, oxjob #475) ------------------
// Move a node — an expr (a `clause` or a clause `group`), OR a value-bag (`vgroup`) —
// to a new position among the children of `targetParentId` at `targetIndex`. The drag
// machinery only ever offers TYPE-COMPATIBLE targets (an expr drops into a clause-list
// `group`; a value-bag drops into a value-list `vgroup`), but we re-check here so the op
// is safe to call directly. Mutates the tree in place; the caller re-renders through the
// server, which re-canonicalizes joins / parens / first-sibling dots. Returns true on a
// real move, false on a no-op / invalid request.
export function moveNode(tree, id, targetParentId, targetIndex, drafts = []) {
  const hit = locate(tree, id, drafts);
  if (!hit) return false;
  const node = hit.node;
  // can't drop a node into itself or anywhere inside its own subtree
  if (id === targetParentId || subtreeHasId(node, targetParentId)) return false;

  // NB: locate() only walks the implicit `where` group's CHILDREN (never the group node
  // itself), so resolve the target container with findNodeById, which checks the `where`
  // root too — the top-level implicit group is the most common drop target.
  const targetNode = findNodeById(tree, targetParentId, drafts);
  if (!targetNode) return false;

  // pick the source parent + verify kind compatibility
  let parent;
  if (node.node === "clause" || node.node === "group") {
    if (targetNode.node !== "group") return false;     // filters only into clause-lists
    parent = findExprParent(tree, id);
  } else if (node.node === "vgroup") {
    if (targetNode.node !== "vgroup") return false;     // value-bags only into value-lists
    parent = findVGroupOf(tree, id, drafts);
  } else {
    return false;                                       // vleaf moves are chip-level (later)
  }
  if (!parent) return false;

  const srcArr = parent.children;
  const dstArr = targetNode.children;
  const sameArr = parent.id === targetNode.id;          // compare by id (reactive-proxy safe)
  const oldIndex = srcArr.findIndex((c) => c.id === id);
  if (oldIndex < 0) return false;

  let idx = targetIndex;
  srcArr.splice(oldIndex, 1);
  // reorder index-fix: removing a node before the insertion point in the SAME array
  // shifts every later slot left by one.
  if (sameArr && oldIndex < idx) idx -= 1;
  if (idx < 0) idx = 0;
  if (idx > dstArr.length) idx = dstArr.length;
  dstArr.splice(idx, 0, node);

  pruneEmpty(tree);
  return true;
}

// Does `id` live anywhere inside `node`'s subtree (excluding `node` itself)? Guards
// moveNode against dropping a node into its own descendant.
function subtreeHasId(node, id) {
  let found = false;
  const visit = (n) => {
    if (found || !n) return;
    if (n.id === id) { found = true; return; }
    if (n.node === "group") n.children.forEach(visit);
    else if (n.node === "clause" && n.value) visit(n.value);
    else if (n.node === "vgroup") n.children.forEach(visit);
  };
  if (node.node === "group") node.children.forEach(visit);
  else if (node.node === "clause" && node.value) visit(node.value);
  else if (node.node === "vgroup") node.children.forEach(visit);
  return found;
}

// Find any node by id, INCLUDING the implicit `where` group root (which locate() skips
// — it only walks that group's children). Used to resolve a move's target container.
function findNodeById(tree, id, drafts = []) {
  let res = null;
  const visit = (n) => {
    if (res || !n) return;
    if (n.id === id) { res = n; return; }
    if (n.node === "group") n.children.forEach(visit);
    else if (n.node === "clause" && n.value) visit(n.value);
    else if (n.node === "vgroup") n.children.forEach(visit);
  };
  if (tree && tree.where) visit(tree.where);
  drafts.forEach((d) => { if (d.value) visit(d.value); });
  return res;
}

// The owning vgroup of a value node (drag-and-drop slot geometry, oxjob #475) —
// a public alias for findVGroupOf so the builder can resolve a value-bag's parent.
export function findValueParent(tree, id, drafts = []) {
  return findVGroupOf(tree, id, drafts);
}

// The clause whose DIRECT value IS the vleaf `id` (a single-value filter like
// `year is 2020` — value is a bare vleaf, no enclosing vgroup), or null when the value
// lives inside a vgroup. Dragging such a sole value OUT empties its clause, so moveValues
// removes the now-value-less clause (it would be an invalid OQO otherwise). (oxjob #475.)
function soleValueClause(tree, id, drafts = []) {
  let res = null;
  const inExpr = (n) => {
    if (res) return;
    if (n.node === "clause") { if (n.value && n.value.node === "vleaf" && n.value.id === id) res = n; }
    else if (n.node === "group") n.children.forEach(inExpr);
  };
  const w = tree && tree.where;
  if (w) { (w.node === "group" && w.implicit) ? w.children.forEach(inExpr) : inExpr(w); }
  drafts.forEach((d) => { if (d.value && d.value.node === "vleaf" && d.value.id === id) res = d; });
  return res;
}

// Document-order list of the value ids in `idSet` (so a multi-chip drag preserves the
// on-screen order of the dragged values when they re-land in the target list). Note a SIMPLE
// (single-value) clause stores its scalar on `leaf` and the value token carries the CLAUSE id
// (no separate vleaf) — so a clause id in the set is itself a draggable value.
function valuesInDocOrder(tree, idSet, drafts = []) {
  const out = [];
  const inValue = (v) => { if (idSet.has(v.id)) out.push(v.id); if (v.node === "vgroup") v.children.forEach(inValue); };
  const inExpr = (n) => {
    if (n.node === "clause") { if (n.value) inValue(n.value); else if (idSet.has(n.id)) out.push(n.id); }
    else if (n.node === "group") n.children.forEach(inExpr);
  };
  const w = tree && tree.where;
  if (w) { (w.node === "group" && w.implicit) ? w.children.forEach(inExpr) : inExpr(w); }
  drafts.forEach((d) => { if (d.value) inValue(d.value); else if (idSet.has(d.id)) out.push(d.id); });
  return out;
}

// Resolve a move's destination value list (a vgroup). A vgroup target drops straight in; a
// single-value `clause` target is PROMOTED to a list first so the dropped chip joins its value
// (`year is 2020` + 2021-drop → `year is (2020 or 2021)`). Both the `value:vleaf` shape and the
// real server `leaf` shape are lifted into a fresh `or`-vgroup. Returns the vgroup, or null.
function resolveDestVgroup(target) {
  if (!target) return null;
  if (target.node === "vgroup") return target;
  if (target.node !== "clause") return null;
  if (target.value && target.value.node === "vgroup") return target.value;
  if (target.value && target.value.node === "vleaf") {
    target.value = { node: "vgroup", id: eid(), join: "or", children: [target.value] };
    return target.value;
  }
  if (target.leaf) {
    const lf = vleaf(target.leaf.value, leafDisplay(target), target.leaf.negated);
    target.value = { node: "vgroup", id: eid(), join: "or", children: [lf] };
    delete target.leaf;
    return target.value;
  }
  return null;
}

// Take the value `id` out of the tree as a standalone vleaf, ready to re-insert elsewhere.
// Two source shapes: a real `vleaf` inside a vgroup (detach it in place), or a SIMPLE clause
// whose scalar lives on `leaf` (the value id IS the clause id) — synthesize a vleaf from the
// leaf and flag the now-value-less clause for removal. Returns { node, removeClause } or null.
function takeValue(tree, id, drafts = []) {
  const hit = locate(tree, id, drafts);
  if (!hit) return null;
  if (hit.kind === "vleaf") {
    const sc = soleValueClause(tree, id, drafts);   // defensive: a `value:vleaf` sole shape
    hit.detach();
    return { node: hit.node, removeClause: sc ? sc.id : null };
  }
  if (hit.kind === "clause" && hit.node.leaf) {
    const lf = vleaf(hit.node.leaf.value, leafDisplay(hit.node), hit.node.leaf.negated);
    return { node: lf, removeClause: hit.node.id };
  }
  return null;
}

// Move a SET of value chips (vleaves) into a target value list at `targetIndex` — the
// chip-level drag-and-drop (oxjob #475, "reverse of dragging rows": a vertical insertion
// line between chips). The dragged values are spliced out of wherever they live (one list
// or several) and re-inserted, in document order, into the destination list:
//   • targetParentId is a `vgroup` → drop straight in;
//   • targetParentId is a single-value `clause` → promote its lone value to a vgroup first
//     (so the dropped chip joins it, `year is 2020` + apple-drop is structurally a list).
// `targetIndex` is the insertion position among the destination's CURRENT children, as the
// drag geometry computed it; we re-anchor to the node at that slot so the index is robust to
// the removals. A dragged value that was a clause's SOLE value leaves that clause empty, so
// we drop the clause. The moved chip adopts the destination list's join (conjunction is a
// vgroup property, not the chip's — Jason 2026-06-17): nothing to copy, it just renders with
// the new parent's join. The server re-canonicalizes on the next swap render. Returns true on
// a real move, false on a no-op / invalid request. Mutates the tree in place.
export function moveValues(tree, ids, targetParentId, targetIndex, drafts = []) {
  const uniq = [...new Set((ids || []).filter((x) => x != null))];
  if (!uniq.length) return false;
  if (uniq.includes(targetParentId)) return false;             // can't drop into one of the dragged chips

  // resolve the destination value list, promoting a single-value clause target to a vgroup.
  const target = findNodeById(tree, targetParentId, drafts);
  const dest = resolveDestVgroup(target);
  if (!dest || dest.node !== "vgroup" || uniq.includes(dest.id)) return false;

  // the node to insert BEFORE (null = append): the first dest child at/after the slot that
  // is NOT itself being dragged — re-found by id after the removals, so splicing is safe.
  let anchor = null;
  for (let i = Math.max(0, targetIndex); i < dest.children.length; i++) {
    if (!uniq.includes(dest.children[i].id)) { anchor = dest.children[i]; break; }
  }

  // take the dragged values out (document order), tracking any source clause emptied by it.
  const ordered = valuesInDocOrder(tree, new Set(uniq), drafts);
  const collected = [];
  const emptiedClauses = [];
  for (const id of ordered) {
    const taken = takeValue(tree, id, drafts);
    if (!taken) continue;
    collected.push(taken.node);
    if (taken.removeClause) emptiedClauses.push(taken.removeClause);
  }
  if (!collected.length) return false;

  const at = anchor ? dest.children.findIndex((c) => c.id === anchor.id) : dest.children.length;
  dest.children.splice(at < 0 ? dest.children.length : at, 0, ...collected);

  // drop clauses whose only value we just moved away (a simple clause's scalar source). locate
  // finds the now-value-less clause + detaches it; pruneEmpty tidies any emptied ancestors.
  emptiedClauses.forEach((cid) => { const h = locate(tree, cid, drafts); if (h) h.detach(); });
  pruneEmpty(tree);
  return true;
}

// Add another value to a value list (Enter / "Add value"). Given any vbrick id
// inside a clause, append an empty sibling vleaf. A simple clause (leaf, single
// value) is promoted to a factored vgroup of two vleaves.
export function addValue(tree, id, drafts = []) {
  const hit = locate(tree, id, drafts);
  if (!hit) return null;
  if (hit.kind === "vgroup") {
    // addressed by the value-group id itself (paren-block `[+]`): append a sibling
    const nv = vleaf(""); hit.node.children.push(nv); return nv.id;
  }
  if (hit.kind === "vleaf") {
    // find the owning vgroup
    const grp = findVGroupOf(tree, id, drafts);
    if (grp) { const nv = vleaf(""); grp.children.push(nv); return nv.id; }
  }
  if (hit.kind === "clause") {
    const c = hit.node;
    if (c.value && c.value.node === "vgroup") {
      const nv = vleaf(""); c.value.children.push(nv); return nv.id;
    }
    if (c.leaf) {
      // promote simple -> factored: existing value becomes a vleaf, add an empty one
      const cur = vleaf(c.leaf.value, leafDisplay(c), c.leaf.is_negated);
      const nv = vleaf("");
      c.value = { node: "vgroup", id: eid(), join: "or", children: [cur, nv] };
      delete c.leaf;
      return nv.id;
    }
  }
  return null;
}

// Insert an empty sibling vleaf immediately AFTER the value `afterId`, within its
// OWN vgroup — so a chip's "New" adds the new chip right to its right (Jason
// 2026-06-16), not at the end of the clause, and respecting the local group (a
// value in a nested `or`-group gets its new sibling inside that same group). For a
// value that isn't a factored vleaf (a simple clause's sole value, addressed by the
// clause id, or a vgroup addressed directly) we fall back to addValue, which
// promotes/appends — the "after" is unambiguous there (one value, or end-of-group).
// Returns the new vleaf id. (oxjob #428.)
export function addValueAfter(tree, afterId, drafts = []) {
  const hit = locate(tree, afterId, drafts);
  if (!hit) return null;
  if (hit.kind === "vleaf") {
    const grp = findVGroupOf(tree, afterId, drafts);
    if (grp) {
      const i = grp.children.findIndex((c) => c.id === afterId);
      const nv = vleaf("");
      grp.children.splice(i < 0 ? grp.children.length : i + 1, 0, nv);
      return nv.id;
    }
  }
  return addValue(tree, afterId, drafts);
}

// Add an empty sibling value immediately AFTER the value-group `groupId`, within that
// group's PARENT vgroup — the nested-bag hover "+" (oxjob #475): a `(a or b)` bag nested
// in `( bagA and bagB )` gets a NEW value as a sibling of the bag, not inside it. Returns
// { id, join } (join = the parent vgroup's connector), or null when `groupId` has no
// enclosing vgroup (it's the clause's own value root — the caller then adds INSIDE it).
export function addSiblingValueAfterGroup(tree, groupId, drafts = []) {
  const parent = findVGroupOf(tree, groupId, drafts);
  if (!parent) return null;
  const i = parent.children.findIndex((c) => c.id === groupId);
  const nv = vleaf("");
  parent.children.splice(i < 0 ? parent.children.length : i + 1, 0, nv);
  return { id: nv.id, join: parent.join || "and" };
}

// Insert an empty value at the FRONT (index 0) of the value-group addressed by `groupId`
// — the join-chip menu's "add value", which inserts where the user's attention is (the start
// of the list) rather than the end (oxjob #475 D3, Jason 2026-06-19). Mirror of
// addSiblingValueAfterGroup but targeting the group's OWN children at index 0. Returns
// { id, join } (join = the group's connector), or null when `groupId` isn't a vgroup. Like the
// other transient inserts the empty vleaf is stripped on round-trip (vFilled), so the caller
// renders it as a pendingScalar box and only commits once the user types.
export function addValueAtFront(tree, groupId, drafts = []) {
  const hit = locate(tree, groupId, drafts);
  const grp = hit && hit.kind === "vgroup" ? hit.node : null;
  if (!grp) return null;
  const nv = vleaf("");
  grp.children.unshift(nv);
  return { id: nv.id, join: grp.join || "or" };
}

// Insert a new empty value ADJACENT to value `valueId`, joined to it by `join`
// ("and"/"or"), honoring standard precedence (oxjob #507 — the per-line `+` menu's
// AND/OR after a value chip). Three cases:
//   - simple clause leaf (the value IS the clause's sole scalar): promote to a vgroup
//     with the chosen join — `cancer` + AND → `(cancer and _)`.
//   - vleaf whose owning vgroup already uses `join`: append an empty sibling after it
//     (same-join run stays flat) — `(a or b)` on b + OR → `(a or b or _)`.
//   - vleaf whose owning vgroup uses the OTHER join: nest this value with the new one
//     in a `join` subgroup (precedence) — `(a or b)` on b + AND → `(a or (b and _))`.
// Returns { id, join } for the new empty value, or null. The empty vleaf is stripped on
// round-trip (vFilled), so the caller renders it transiently until a value is typed/picked.
export function addAdjacentValue(tree, valueId, join, drafts = []) {
  const hit = locate(tree, valueId, drafts);
  if (!hit) return null;
  if (hit.kind === "clause" && hit.node.leaf) {
    const c = hit.node;
    const cur = vleaf(c.leaf.value, leafDisplay(c), c.leaf.is_negated);
    const nv = vleaf("");
    c.value = { node: "vgroup", id: eid(), join, children: [cur, nv] };
    delete c.leaf;
    return { id: nv.id, join };
  }
  if (hit.kind === "vleaf") {
    const grp = findVGroupOf(tree, valueId, drafts);
    if (!grp) return null;
    const i = grp.children.findIndex((c) => c.id === valueId);
    if ((grp.join || "or") === join) {
      const nv = vleaf("");
      grp.children.splice(i < 0 ? grp.children.length : i + 1, 0, nv);
      return { id: nv.id, join };
    }
    const empty = vleaf("");
    const ng = { node: "vgroup", id: eid(), join, children: [hit.node, empty] };
    grp.children.splice(i < 0 ? grp.children.length : i, i < 0 ? 0 : 1, ng);
    return { id: empty.id, join };
  }
  return null;
}

// Append a new empty value at the END of the vgroup that CONTAINS `memberId` (a vleaf id, or
// a nested vgroup id such as a text-block AND sub-group), joined by `join`. This is the
// end-of-line "or" target (#575 round 8, Jason): a new term must land after EVERY existing
// operand — including value-block AND sub-groups that sort AFTER the plain values in the bag —
// so the draft appears at the very end, not mid-list between the last plain value and the first
// block. When the container's join differs (or the member isn't in a vgroup), fall back to
// addAdjacentValue, which nests by precedence. Returns { id, join } or null.
export function appendAdjacentValue(tree, memberId, join, drafts = []) {
  const grp = findVGroupOf(tree, memberId, drafts);
  if (grp && (grp.join || "or") === join) {
    const nv = vleaf("");
    grp.children.push(nv);
    return { id: nv.id, join };
  }
  return addAdjacentValue(tree, memberId, join, drafts);
}

// Prepend a new empty value to the FRONT of clause `clauseId`'s value bag, joined by
// `join` (oxjob #507 — the per-line `+` menu's AND/OR on a value-bag HEADER line, which
// adds to the TOP of the field's value list). Same-join bag → unshift a sibling at index
// 0; other-join → wrap the whole bag in a `join` vgroup with the new value at the front
// (`(a or b)` + AND-front → `(_ and (a or b))`). A simple clause degrades to
// addAdjacentValue (one value: front ≈ after). Returns { id, join } or null.
export function prependBagValue(tree, clauseId, join, drafts = []) {
  const hit = locate(tree, clauseId, drafts);
  if (!hit || hit.kind !== "clause") return null;
  const c = hit.node;
  if (c.value && c.value.node === "vgroup") {
    const bag = c.value;
    if ((bag.join || "or") === join) {
      const nv = vleaf(""); bag.children.unshift(nv); return { id: nv.id, join };
    }
    const empty = vleaf("");
    c.value = { node: "vgroup", id: eid(), join, children: [empty, bag] };
    return { id: empty.id, join };
  }
  if (c.leaf) return addAdjacentValue(tree, c.id, join, drafts);
  if (c.value && c.value.node === "vleaf") return addAdjacentValue(tree, c.value.id, join, drafts);
  return null;
}

// Append a new empty AND-ROW to clause `clauseId`'s WHOLE value — the bottom-edge "& +"
// add-row target and ⇧Enter (oxjob #523 Phase 4, AND=down). Unlike addAdjacentValue (which
// tight-binds a new term to ONE neighbouring value), this AND-s the new value with the ENTIRE
// existing value, producing a new top-level value row:
//   `(apple or banana)` → `((apple or banana) and _)`   [wrap the whole OR group]
//   `apple`             → `(apple and _)`                [promote a simple/single value]
//   `(a and b)`         → `(a and b and _)`              [already an AND bag: flat append]
// Returns { id, join:"and" } for the new empty value, or null. The empty vleaf is stripped on
// round-trip (vFilled) like every other transient insert, so the caller renders it as a
// pendingScalar box until a value is typed/picked.
export function addAndRow(tree, clauseId, drafts = []) {
  const hit = locate(tree, clauseId, drafts);
  if (!hit || hit.kind !== "clause") return null;
  const c = hit.node;
  const nv = vleaf("");
  // already a top-level AND bag → just append a flat sibling row.
  if (c.value && c.value.node === "vgroup" && (c.value.join || "or") === "and") {
    c.value.children.push(nv);
    return { id: nv.id, join: "and" };
  }
  // otherwise wrap the existing whole value (an OR vgroup, a single vleaf, or a promoted
  // simple leaf) as the first operand of a new AND vgroup, with the empty value second.
  let cur;
  if (c.value) cur = c.value;
  else if (c.leaf) cur = vleaf(c.leaf.value, leafDisplay(c), c.leaf.is_negated);
  else return null;
  c.value = { node: "vgroup", id: eid(), join: "and", children: [cur, nv] };
  delete c.leaf;
  return { id: nv.id, join: "and" };
}

// The join ("and"/"or") of the vgroup that directly owns value `id` — used to render
// the leading connector for a transient empty value box added inside a nested group
// (#472, committed-tree scalar "New"). Defaults to "or" when the value isn't in a
// vgroup (a simple clause's sole value).
export function joinOfValue(tree, id, drafts = []) {
  const g = findVGroupOf(tree, id, drafts);
  return (g && g.join) || "or";
}

// Wrap value `id` in a NEW nested vgroup seeded with the value plus an empty sibling —
// the value-chip "Group" gesture (#472 gesture 2). e.g. `(Boy and Girl and cat)` → click
// Girl → `(Boy and (Girl or _) and cat)`. The inner group's join defaults to the OPPOSITE
// of the owning group's join, since that's the only nesting that changes meaning (the
// canonical `A and (B or C)`); a same-join nest would just re-flatten on canonicalization.
// Only meaningful for a factored `vleaf` that already sits in a vgroup (real nesting);
// returns null otherwise (a simple clause's lone value has nothing to nest WITHIN — the
// caller falls back to a plain add). Like the scalar "New" pending box, the empty sibling
// is stripped on round-trip (vFilled) and the singleton vgroup would then collapse, so the
// caller must render this transiently (pendingGroup) and only commit once the 2nd value is
// typed. Returns { innerId, emptyId, innerJoin }.
export function wrapValueInGroup(tree, id, drafts = []) {
  const hit = locate(tree, id, drafts);
  if (!hit || hit.kind !== "vleaf") return null;
  const grp = findVGroupOf(tree, id, drafts);
  if (!grp) return null;                       // a vleaf is always inside a vgroup; guard anyway
  const innerJoin = grp.join === "and" ? "or" : "and";
  const empty = vleaf("");
  const ng = { node: "vgroup", id: eid(), join: innerJoin, children: [hit.node, empty] };
  const i = grp.children.findIndex((c) => c.id === id);
  grp.children.splice(i, 1, ng);               // replace the value in place with the wrapper
  return { innerId: ng.id, emptyId: empty.id, innerJoin };
}

function findVGroupOf(tree, childId, drafts = []) {
  let res = null;
  const inV = (v) => {
    if (res) return;
    if (v.node === "vgroup") {
      if (v.children.some((c) => c.id === childId)) { res = v; return; }
      v.children.forEach(inV);
    }
  };
  const inE = (n) => {
    if (res) return;
    if (n.node === "clause" && n.value) inV(n.value);
    if (n.node === "group") n.children.forEach(inE);
  };
  const w = tree && tree.where;
  if (w) { (w.node === "group" && w.implicit) ? w.children.forEach(inE) : inE(w); }
  drafts.forEach((d) => { if (d.value) inV(d.value); });
  return res;
}

// ---- multi-select: group / delete a SET of values (oxjob #472) --------------
// Selecting several value chips (Shift/Cmd-click) and "Add to subclause" wraps them
// into a new nested vgroup at their LOWEST COMMON ANCESTOR vgroup. Same-field only
// (Jason 2026-06-16): every selected value must be a vleaf in the SAME clause, so the
// new subclause is a value-group `col is (…)` — cross-field grouping is a follow-up.
// Unlike the single-value "Group" gesture (wrapValueInGroup), every selected value is
// already FILLED, so the wrap produces a valid OQO directly — NO transient pending box:
// the caller just renderQuery({swap:true}) and the server canonicalizes (it collapses
// the now-singleton leftover vgroups, e.g. `(Boy or X)` minus Boy → X).

// The chain of vgroup NODES from a clause's value root down to the vgroup that directly
// owns `id` (inclusive), or null if `id` isn't a vleaf anywhere under `vroot`.
function leafChain(vroot, id) {
  if (!vroot || vroot.node !== "vgroup") return null;
  if (vroot.children.some((c) => c.id === id)) return [vroot];
  for (const c of vroot.children) {
    if (c.node === "vgroup") {
      const sub = leafChain(c, id);
      if (sub) return [vroot, ...sub];
    }
  }
  return null;
}

// Locate a vleaf's clause + its vgroup-ancestor chain. Returns { clause, columnId, chain }
// or null (id not a factored vleaf — e.g. a simple clause's sole value has no vgroup, so
// there's nothing to group it within).
function leafContext(tree, id, drafts = []) {
  let res = null;
  const inClause = (c) => {
    if (res || !c.value || c.value.node !== "vgroup") return;
    const chain = leafChain(c.value, id);
    if (chain) res = { clause: c, columnId: c.column_id, chain };
  };
  const inExpr = (n) => {
    if (res) return;
    if (n.node === "clause") inClause(n);
    else if (n.node === "group") n.children.forEach(inExpr);
  };
  const w = tree && tree.where;
  if (w) { (w.node === "group" && w.implicit) ? w.children.forEach(inExpr) : inExpr(w); }
  drafts.forEach(inClause);
  return res;
}

function containsValueId(v, id) {
  if (!v) return false;
  if (v.id === id) return true;
  return v.node === "vgroup" && v.children.some((c) => containsValueId(c, id));
}

// Can the given value ids be wrapped into ONE subclause? Yes iff there are ≥2 distinct
// of them and all are factored vleaves in the SAME clause (same field). Returns
// { clause, columnId, lca } (lca = the deepest shared vgroup node) or null. The builder
// uses this to enable/disable the "Add to subclause" menu item. (Same-field-only, #472.)
export function groupableValues(tree, ids, drafts = []) {
  const uniq = [...new Set((ids || []).filter((x) => x != null))];
  if (uniq.length < 2) return null;
  const ctxs = uniq.map((id) => leafContext(tree, id, drafts));
  if (ctxs.some((c) => !c)) return null;
  const clause = ctxs[0].clause;
  if (ctxs.some((c) => c.clause !== clause)) return null;     // same field only
  // LCA = longest common prefix of the (root-anchored) vgroup chains.
  let lca = ctxs[0].chain[0];
  for (let d = 1; ; d++) {
    const node = ctxs[0].chain[d];
    if (!node || ctxs.some((c) => c.chain[d] !== node)) break;
    lca = node;
  }
  return { clause, columnId: ctxs[0].columnId, lca };
}

// Wrap the selected values into a new nested vgroup inserted at their LCA ("Add to
// subclause", #472). The new group's join is the OPPOSITE of the LCA's join — the only
// nesting that survives canonicalization as a real precedence boundary (a same-join nest
// just re-flattens). The selected leaves are MOVED out of their original positions into
// the new group in document order; leftover singletons (e.g. `(Boy or X)` minus Boy) are
// collapsed by the server on the next render. Returns the new vgroup id, or null.
export function wrapValuesInGroup(tree, ids, drafts = []) {
  const info = groupableValues(tree, ids, drafts);
  if (!info) return null;
  const { lca } = info;
  const idset = new Set(ids);
  // document-order list of the selected leaf NODES under the clause's value root
  const ordered = [];
  const collect = (v) => {
    if (v.node === "vgroup") v.children.forEach(collect);
    else if (idset.has(v.id)) ordered.push(v);
  };
  collect(info.clause.value);
  if (ordered.length < 2) return null;
  // insertion slot: the LCA-direct-child subtree that holds the FIRST selected leaf. A
  // placeholder reserves it while we detach the leaves (some may be direct children of
  // the LCA), so the index stays valid; we re-find the placeholder by its unique id after.
  // Find by id, NOT by reference: on a Vue REACTIVE tree the spliced-in object is wrapped
  // in a proxy, so `c === placeholder` is false (proxy ≠ raw) → findIndex returns -1 →
  // splice(-1,…) clobbers the LAST child and strands the empty placeholder. The id is a
  // plain string, unaffected by proxying. (Plain-object unit tests never hit this.)
  const insAt = lca.children.findIndex((child) => containsValueId(child, ordered[0].id));
  const phId = eid();
  lca.children.splice(insAt < 0 ? lca.children.length : insAt, 0,
    { node: "vgroup", id: phId, join: lca.join, children: [] });
  ordered.forEach((leaf) => {
    const parent = findVGroupOf(tree, leaf.id, drafts);
    if (parent) { const i = parent.children.findIndex((c) => c.id === leaf.id); if (i >= 0) parent.children.splice(i, 1); }
  });
  const ng = { node: "vgroup", id: eid(), join: lca.join === "and" ? "or" : "and", children: ordered };
  const pi = lca.children.findIndex((c) => c.id === phId);
  lca.children.splice(pi, 1, ng);
  return ng.id;
}

// Remove a SET of nodes by id, pruning once at the end (batch "Delete n chips", #472).
// Each id is located fresh against the current tree state, so detaches don't invalidate
// one another's indices.
export function removeNodes(tree, ids, drafts = []) {
  [...new Set((ids || []).filter((x) => x != null))].forEach((id) => {
    const hit = locate(tree, id, drafts);
    if (hit) hit.detach();
  });
  pruneEmpty(tree);
}

// ---- multi-select: group a SET of whole FILTERS into a clause group ----------
// (oxjob #472, unblocks #428 Phase B). The clause-level analog of wrapValuesInGroup:
// select ≥2 whole filters by their FIELD chip and wrap them into a parenthesized
// CLAUSE group, e.g. `type is article and year is 2020` → select both →
// `(type is article or year is 2020)`. Unlike value grouping this works ACROSS fields
// — a clause group's children are independent filters, not values of one column. The
// new group's join is the OPPOSITE of the parent's (an `and` parent → an `or` group),
// the only nesting that survives canonicalization as a real precedence boundary (a
// same-join nest just re-flattens). Every selected filter is already complete, so the
// wrap produces a valid OQO directly — no transient pending box; the caller just
// renderQuery({swap:true}) and the server re-canonicalizes.

// The expr node (clause | group) for `id` plus the expr GROUP that directly contains it
// (its sibling list), or null if `id` isn't a committed expr node. The top-level implicit
// `where` group counts as the parent of the root filter rows. Drafts are excluded — a
// half-built clause isn't a committed filter row.
function exprContext(tree, id) {
  let res = null;
  const walk = (n, parent) => {
    if (res) return;
    if ((n.node === "clause" || n.node === "group") && n.id === id && !n.draft) {
      res = { node: n, parent };
      return;
    }
    if (n.node === "group") n.children.forEach((c) => walk(c, n));
  };
  const w = tree && tree.where;
  if (w) { (w.node === "group" && w.implicit) ? w.children.forEach((c) => walk(c, w)) : walk(w, null); }
  return res;
}

// The effective join of an expr group: the implicit top-level `where` group is an AND
// (its children are filter_rows, joined implicitly), an explicit group carries its own.
function exprGroupJoin(g) {
  return g && g.implicit ? "and" : ((g && g.join) || "and");
}

// Can the given ids be wrapped into ONE clause group? Yes iff there are ≥2 distinct of
// them and all are committed expr nodes (clauses/groups) that are SIBLINGS — direct
// children of the same expr group. Returns { parent } (the shared parent group) or null.
// The builder uses this to enable/disable "Wrap as subclause" for a whole-filter selection.
export function groupableFilters(tree, ids) {
  const uniq = [...new Set((ids || []).filter((x) => x != null))];
  if (uniq.length < 2) return null;
  const ctxs = uniq.map((id) => exprContext(tree, id));
  if (ctxs.some((c) => !c || !c.parent)) return null;
  const parent = ctxs[0].parent;
  if (ctxs.some((c) => c.parent !== parent)) return null;  // must be siblings
  return { parent };
}

// Wrap the selected filters into a new clause group inserted at their shared parent
// ("Wrap as subclause" for a whole-filter selection, #472). The new group's join is the
// OPPOSITE of the parent's. The selected expr nodes are MOVED out of their positions into
// the new group in document order; any non-selected siblings stay put. Returns the new
// group id, or null.
export function wrapFiltersInGroup(tree, ids) {
  const info = groupableFilters(tree, ids);
  if (!info) return null;
  const { parent } = info;
  const idset = new Set(ids);
  // document-order list of the selected child nodes (direct children of the parent)
  const ordered = parent.children.filter((c) => idset.has(c.id));
  if (ordered.length < 2) return null;
  const groupJoin = exprGroupJoin(parent) === "and" ? "or" : "and";
  // Insert a placeholder at the FIRST selected child's slot, then detach the selected
  // children (re-find each by id so shifting indices stay valid), then swap the
  // placeholder for the real group. Find by id, NOT by reference: on a Vue REACTIVE tree
  // a spliced-in raw object reads back as its proxy, so `c === placeholder` is false →
  // findIndex returns -1 → splice(-1,…) clobbers the wrong child. (Same trap as
  // wrapValuesInGroup; plain-object unit tests never hit it.)
  const insAt = parent.children.findIndex((c) => c.id === ordered[0].id);
  const phId = eid();
  parent.children.splice(insAt < 0 ? parent.children.length : insAt, 0,
    { node: "group", id: phId, join: groupJoin, paren: true, children: [] });
  ordered.forEach((node) => {
    const i = parent.children.findIndex((c) => c.id === node.id);
    if (i >= 0) parent.children.splice(i, 1);
  });
  const ng = { node: "group", id: eid(), join: groupJoin, paren: true, children: ordered };
  const pi = parent.children.findIndex((c) => c.id === phId);
  parent.children.splice(pi, 1, ng);
  return ng.id;
}

// OR a COMPLETED draft onto a top-level row (the ghost `or…` fold, oxjob #595). Two cases:
//   - `targetId` is already a flat or-group → the draft appends as its new last disjunct.
//   - `targetId` is a TOP-LEVEL clause → wrap it in a fresh {join:"or", paren:true} group
//     in place, then append the draft. Wrapping is restricted to top-level clauses (parent
//     = the implicit root, or the clause IS the bare `where`) — wrapping a nested node
//     would produce a shape representableShape's orRowsOk bounces to the OQL tab.
// The tree is only mutated on success-path fold (an abandoned draft never wraps anything).
// Returns true when the draft was placed; the caller drops it from the draft list.
export function orDraftOntoRow(tree, targetId, draft, drafts = []) {
  const ctx = exprContext(tree, targetId);
  if (!ctx) return false;
  let g = null;
  if (ctx.node.node === "group" && ctx.node.join === "or") {
    g = ctx.node;
  } else if (ctx.node.node === "clause"
             && (ctx.parent == null || ctx.parent.implicit)) {
    const wrapper = { node: "group", id: eid(), join: "or", paren: true, children: [ctx.node] };
    if (ctx.parent) {
      // find by id, NOT reference — Vue's reactive tree returns proxies (see wrapFiltersInGroup)
      const i = ctx.parent.children.findIndex((c) => c.id === targetId);
      if (i < 0) return false;
      ctx.parent.children.splice(i, 1, wrapper);
    } else {
      tree.where = wrapper; // the sole top-level filter
    }
    g = wrapper;
  } else {
    return false;
  }
  return placeDraftInTree(tree, draft, g.id, g.children.length, drafts);
}

// Remove ONE disjunct from a flat or-group (the per-disjunct trash, oxjob #595). Deleting
// down to a single disjunct DISSOLVES the group — the survivor takes the group's slot, so
// no single-child wrapper is left behind (pruneEmpty deliberately doesn't collapse those:
// a negation wrapper is a legitimate single-child group). Negated groups are left intact —
// dissolving one would silently drop the NOT.
export function removeDisjunct(tree, clauseId, drafts = []) {
  const parent = findExprParent(tree, clauseId);
  removeNode(tree, clauseId, drafts);
  if (!parent || parent.node !== "group" || parent.join !== "or" || parent.negated) return;
  if (parent.children.length !== 1) return;
  const survivor = parent.children[0];
  const gp = findExprParent(tree, parent.id);
  if (gp && gp.node === "group") {
    const i = gp.children.findIndex((c) => c.id === parent.id);
    if (i >= 0) gp.children.splice(i, 1, survivor);
  } else if (tree.where && tree.where.id === parent.id) {
    tree.where = survivor;
  }
}

// ---- drafts (clause creation) ----------------------------------------------

export function makeDraft() {
  return { node: "clause", id: eid(), draft: true, column_id: null,
           column: null, operator: "is", clause_kind: "other",
           unary: false, numeric: false, value: null, editing: false };
}

// Pop an already-committed FLAT scalar clause into an "editing" draft that carries
// its existing values plus a fresh empty one. This is how you add another value to
// a committed scalar clause: the server-lines model can't render an empty
// intermediate (the server only ever lays out COMPLETE queries), so we move the
// whole clause into the local draft layer — which DOES render incomplete clauses —
// until the new value is filled. The `editing` flag keeps the draft from folding
// back into the query on the pop-time re-render (see OqlQueryBuilder currentOqo /
// renderQuery); it's cleared on blur, after which the draft folds canonically.
// Entity values don't need this (they're picked atomically, never empty).
// `afterId` (optional): insert the new empty value right after that EXISTING value
// (the clicked chip), so scalar "New" adds the box to the chip's right rather than
// at the clause end (oxjob #428, Jason 2026-06-16). Only meaningful for a FLAT
// clause — the caller guards nested clauses out (the flat-vgroup draft model can't
// represent nesting), so `collect` here only ever sees a single level.
export function popClauseToDraft(tree, clauseId, drafts, { column, kind, op, afterId } = {}) {
  const hit = locate(tree, clauseId, drafts);
  if (!hit || hit.kind !== "clause") return null;
  const c = hit.node;
  const existing = [];
  let insertIdx = -1;
  const pushOne = (origId, vl) => { existing.push(vl); if (origId != null && origId === afterId) insertIdx = existing.length; };
  // a simple clause's sole value is addressed by the CLAUSE id (see locate's header)
  if (c.leaf) pushOne(c.id, vleaf(c.leaf.value, leafDisplay(c), c.leaf.is_negated));
  else if (c.value) {
    const collect = (v) => {
      if (v.node === "vleaf") pushOne(v.id, vleaf(v.value, v.display, v.negated));
      else (v.children || []).forEach(collect);
    };
    (c.value.children || []).forEach(collect);
  }
  const empty = vleaf("");
  if (insertIdx >= 0) existing.splice(insertIdx, 0, empty); else existing.push(empty);
  const d = makeDraft();
  d.column_id = c.column_id;
  d.column = column || c.column || c.column_id;
  d.operator = op || c.operator || "is";
  d.clause_kind = kind === "entity" ? "entity" : kind === "boolean" ? "bool" : "other";
  d.numeric = kind === "number";
  d.value = { node: "vgroup", id: eid(), join: (c.value && c.value.join) || "or",
              children: existing };
  d.editing = true;
  hit.detach();          // remove the committed clause from the tree
  pruneEmpty(tree);
  drafts.push(d);
  return { draft: d, newId: empty.id };
}

// Fill a draft when a field is picked. kind/op come from the property catalog
// (computed by the caller via oqoTree helpers).
export function draftSetField(draft, { column_id, column, kind, op, unary }) {
  draft.column_id = column_id;
  draft.column = column;
  draft.operator = op || "is";
  draft.clause_kind = kind === "entity" ? "entity" : kind === "boolean" ? "bool" : "other";
  draft.unary = !!unary;
  draft.numeric = kind === "number";
  if (unary) { draft.value = null; return; }
  // entity values are PICKED (atomic, never an empty intermediate) → start empty
  // and let the picker add them; scalar/boolean start with one editable brick.
  const children = kind === "entity" ? []
    : [kind === "boolean" ? vleaf(true, "true") : vleaf("")];
  draft.value = { node: "vgroup", id: eid(), join: "or", children };
}

export function draftSetOperator(draft, { op, unary }) {
  draft.operator = op || "is";
  draft.unary = !!unary;
  if (unary) draft.value = null;
  else if (!draft.value)
    draft.value = { node: "vgroup", id: eid(), join: "or", children: [vleaf("")] };
}

// A draft is "complete" (ready to fold into the query) when it has a column and
// either is unary, or carries at least one non-empty value.
export function draftComplete(draft) {
  if (!draft.column_id) return false;
  if (draft.unary) return true;
  // A numeric expression draft (#527) carries a pre-built OQO filter (draftToFilter
  // returns it verbatim); the raw value box is irrelevant once parsed.
  if (draft._numericFilter) return true;
  return !!(draft.value && draft.value.children.some(vFilled));
}
// `value: null` is the null sentinel (`unknown`, #554) — a real value; only "" is a
// blank box (matches vFilled in v2ToOqo).
function vFilled(v) {
  return v.node === "vgroup" ? v.children.some(vFilled) : v.value !== "";
}

// A completed draft -> an OQO filter (leaf or same-column branch), to append to
// the query's filter_rows. Mirrors v2ToOqo's value reconstruction.
export function draftToFilter(draft) {
  // A numeric-expression draft (#527) already parsed to a complete OQO filter (a single
  // leaf, an OR value-list, or an AND group for a range / `and`). The server flattens a
  // top-level AND group into separate filter rows on the next render.
  if (draft._numericFilter) return draft._numericFilter;
  const col = draft.column_id;
  const op = draft.operator || "is";
  if (draft.unary)
    return { column_id: col, value: null, operator: op };
  const vToF = (v) => {
    if (v.node === "vleaf") {
      if (draft.clause_kind === "other" && isSearchColumn(col)) {
        // Route off the surface (display when present), mirroring v2ToOqo.valueToFilter —
        // a popped-in committed value carries its baked `stemmed "…"`/`"…"` display, and
        // routing off the bare value would flip its exactness (#560 Phase 3).
        const surface = typeof v.display === "string" && v.value != null ? v.display : v.value;
        const r = searchSurfaceToFilter(surface, col);
        // `has` is the ONLY valid operator on a search column, and an OMITTED
        // operator defaults server-side to `is` → invalid_operator_for_column.
        // A draft can carry `is` here when its field pick couldn't resolve the
        // property (catalog not yet loaded / unresolvable key) — coerce (#603 r30).
        const o = { column_id: r.column_id, value: r.value,
                    operator: !op || op === "is" ? "has" : op };
        if (v.negated) o.is_negated = true;
        return o;
      }
      const o = { column_id: col, value: v.value, operator: op, is_negated: !!v.negated };
      return o;
    }
    return { join: v.join, filters: v.children.filter(vFilled).map(vToF) };
  };
  const filled = draft.value.children.filter(vFilled);
  if (filled.length === 1) return vToF(filled[0]);
  return { join: draft.value.join, filters: filled.map(vToF) };
}

// ---- numeric value-expression application (oxjob #527) ----------------------
// A range-capable numeric filter shows only `is`; the user types a numeric expression
// (numericExpr.parseNumericExpr) that parses to one or more comparison filters. These two
// helpers turn that parsed `filters` list ([{op, values}]) into the builder's two forms:
//   - draft (new filter):  numericFiltersToOqo -> an OQO filter set on draft._numericFilter,
//                          folded by draftToFilter on the next render.
//   - committed clause:    applyNumericExpr mutates the tree clause in place (and spawns
//                          AND-sibling clauses for a range / `and`).
// One comparison -> a single leaf; a value list (op:"is", multiple values) -> an OR group;
// multiple filters (range / `and`) -> a top-level AND group the server flattens into rows.

// Build the OQO filter for one parsed filter ({op, values}) on `columnId`.
function numericFilterToLeaf(f, columnId) {
  if (f.values.length === 1)
    return { column_id: columnId, value: f.values[0], operator: f.op };
  return { join: "or", filters: f.values.map((v) => ({ column_id: columnId, value: v, operator: "is" })) };
}

// The whole parsed `filters` list -> one OQO filter (an AND group when there's >1).
export function numericFiltersToOqo(filters, columnId) {
  if (!filters || !filters.length) return null;
  if (filters.length === 1) return numericFilterToLeaf(filters[0], columnId);
  return { join: "and", filters: filters.map((f) => numericFilterToLeaf(f, columnId)) };
}

// Set a committed clause (addressed by clauseId) to one parsed filter — a simple leaf for a
// single value, or a factored OR vgroup for a value list.
function setClauseToNumeric(clause, f, columnId, columnName) {
  if (f.values.length === 1) {
    clause.operator = f.op;
    delete clause.value;
    clause.leaf = { column_id: columnId, value: f.values[0], operator: f.op };
  } else {
    clause.operator = "is";
    delete clause.leaf;
    clause.value = { node: "vgroup", id: eid(), join: "or",
                     children: f.values.map((v) => vleaf(v)) };
  }
  if (columnName != null) clause.column = columnName;
}

// Apply a parsed numeric expression to a COMMITTED clause: the clause becomes filters[0];
// any further filters (a range's second half, or `and`-joined values) are inserted as
// AND-sibling clauses on the same column. The server re-canonicalizes on the next render.
export function applyNumericExpr(tree, clauseId, filters, drafts = []) {
  if (!filters || !filters.length) return false;
  const hit = locate(tree, clauseId, drafts);
  if (!hit || hit.kind !== "clause") return false;
  const c = hit.node;
  const col = c.column_id;
  const colName = c.column;
  setClauseToNumeric(c, filters[0], col, colName);
  if (filters.length === 1) return true;
  const extras = filters.slice(1).map((f) => {
    const clause = { node: "clause", id: eid(), column_id: col, column: colName, operator: "is" };
    setClauseToNumeric(clause, f, col, colName);
    return clause;
  });
  const parent = findExprParent(tree, clauseId);
  if (parent && parent.node === "group") {
    const i = parent.children.findIndex((x) => x.id === clauseId);
    const at = i < 0 ? parent.children.length : i + 1;
    parent.children.splice(at, 0, ...extras);
  } else {
    // c is the sole top-level `where` — wrap it + the extras into the implicit AND group.
    tree.where = { node: "group", id: eid(), join: "and", implicit: true, children: [c, ...extras] };
  }
  return true;
}

// ---- click-the-gap insertion (oxjob #494) ----------------------------------
// Insert an empty VALUE (vleaf) into the value-list `parentId` at `index`. `parentId`
// is a vgroup id, or a single-value clause id (then we PROMOTE the lone value to a
// vgroup with the empty inserted at `index`). The empty vleaf renders as an editable
// box (text/number) or an entity placeholder+picker; it's stripped on round-trip
// (vFilled) until filled — same Option-B model as addValueAfter / pendingScalar. The
// caller keeps it local (swap:false renders) until commit. Returns { id, join } or null.
export function insertValueAt(tree, parentId, index, drafts = []) {
  const hit = locate(tree, parentId, drafts);
  if (!hit) return null;
  const ins = (arr, nv) => { let i = index; if (i < 0) i = 0; if (i > arr.length) i = arr.length; arr.splice(i, 0, nv); };
  if (hit.kind === "vgroup") {
    const nv = vleaf(""); ins(hit.node.children, nv);
    return { id: nv.id, join: hit.node.join || "or" };
  }
  if (hit.kind === "clause") {
    const c = hit.node;
    if (c.value && c.value.node === "vgroup") {
      const nv = vleaf(""); ins(c.value.children, nv);
      return { id: nv.id, join: c.value.join || "or" };
    }
    // simple/single-value clause -> promote to a vgroup, empty inserted at `index`
    const cur = c.leaf ? vleaf(c.leaf.value, leafDisplay(c), c.leaf.is_negated)
      : (c.value && c.value.node === "vleaf" ? c.value : null);
    if (cur) {
      const nv = vleaf("");
      const children = index <= 0 ? [nv, cur] : [cur, nv];
      c.value = { node: "vgroup", id: eid(), join: "or", children };
      delete c.leaf;
      return { id: nv.id, join: "or" };
    }
  }
  return null;
}

// Splice a COMPLETED draft (a clause-shaped object) into the filter-list `groupId` at
// `index` — the commit step of a click-the-gap FILTER insert (oxjob #494). Drafts are
// rendered/edited locally (incl. the entity placeholder+picker); on completion we move
// the clause INTO the tree at the gap so `v2ToOqo` serializes it IN POSITION (vs. the
// default `currentOqo` append). Returns true on success. The caller drops the draft
// from its list and re-renders. `groupId` must be a group node (incl. the implicit root).
export function placeDraftInTree(tree, draft, groupId, index, drafts = []) {
  const g = findNodeById(tree, groupId, drafts);
  if (!g || g.node !== "group") return false;
  const clause = { node: "clause", id: draft.id, column_id: draft.column_id,
    column: draft.column, operator: draft.operator || "is" };
  if (draft.unary) clause.value = null;
  else if (draft.value) clause.value = draft.value;
  let i = index; if (i < 0) i = 0; if (i > g.children.length) i = g.children.length;
  g.children.splice(i, 0, clause);
  return true;
}
